/**
 * Token-Schätzung für noch nicht hochgeladene Dokumente.
 *
 * Hintergrund: Die Anzeige im Upload-Dialog soll schon VOR dem Upload zeigen, wie viel
 * Dokument-Budget eine Auswahl belegt. Eine reine Dateigrößen-Rechnung ist dafür bei PDFs
 * unbrauchbar - PDF-Bytes bestehen überwiegend aus eingebetteten Schriften, Bildern und
 * komprimierten Streams, der extrahierbare Text liegt oft unter 1% der Dateigröße.
 * Deshalb wird bei PDFs die Seitenzahl aus der Datei gelesen und mit einem Token-Wert pro
 * Seite multipliziert - dieselbe Logik, die der Server nach dem Upload verwendet.
 *
 * Server-Gegenstücke:
 *   - Vision (force_ai_vision=true): estimate_image_tokens() in token_counter.py,
 *     A4 @ 200 DPI (1654x2339 px) -> 8 Tiles * 170 + 85 = 1445 Tokens/Seite.
 *   - Text (Standard): total_chars // 4 in router.py; eine A4-Textseite hat ~2.800 Zeichen
 *     -> ~700 Tokens/Seite.
 */

// Tokens pro PDF-Seite, je nach Verarbeitungsmodus der App
export const PDF_VISION_TOKENS_PER_PAGE = 1445;
export const PDF_TEXT_TOKENS_PER_PAGE = 700;

// Fallback, wenn die Seitenzahl nicht aus der Datei gelesen werden kann (z.B. verschlüsselte
// PDFs oder PDFs mit komprimierten Objekt-Streams): grobe Schätzung über die Dateigröße.
// Text-PDFs liegen bei ~30-100 KB/Seite, Scans deutlich darüber - 100 KB ist ein Mittelwert.
const PDF_FALLBACK_BYTES_PER_PAGE = 100 * 1024;

// Ab dieser Größe wird die Datei nicht mehr komplett im Browser durchsucht (Speicher),
// sondern direkt über die Dateigröße geschätzt.
const MAX_PARSE_BYTES = 25 * 1024 * 1024;

/**
 * Bytes pro Token für Formate, bei denen die Dateigröße grob mit der Textmenge korreliert.
 * ZIP-basierte Office-Formate (docx/xlsx) enthalten komprimiertes XML plus Styles/Medien,
 * deshalb liegen sie deutlich über den 4 Bytes/Token von reinem Text.
 */
const BYTES_PER_TOKEN_BY_EXT = {
  '.txt': 4,
  '.eml': 4,
  '.msg': 15,   // OLE-Container mit RTF-Variante und Anhängen
  '.doc': 8,    // binäres Word-Format
  '.docx': 15,  // ZIP + XML-Overhead
  '.xls': 8,
  '.xlsx': 15,
};
const DEFAULT_BYTES_PER_TOKEN = 10;

export const getFileExtension = (file) => {
  const name = (file?.name || '').toLowerCase();
  const dotIndex = name.lastIndexOf('.');
  return dotIndex >= 0 ? name.slice(dotIndex) : '';
};

/** Stabiler Schlüssel für den Estimate-Cache (File-Objekte sind bei jedem Render identisch, aber nicht vergleichbar). */
export const getFileKey = (file) =>
  `${file?.name || ''}|${file?.size || 0}|${file?.lastModified || 0}`;

/** Schätzung über die Dateigröße - für alles außer PDF, und als PDF-Fallback. */
const estimateBySize = (file) => {
  const bytesPerToken = BYTES_PER_TOKEN_BY_EXT[getFileExtension(file)] || DEFAULT_BYTES_PER_TOKEN;
  return Math.round((file?.size || 0) / bytesPerToken);
};

/**
 * Liest die Seitenzahl aus den PDF-Bytes.
 *
 * Zwei Strategien, weil PDFs sehr unterschiedlich aufgebaut sind:
 *   1. Seiten-Objekte zählen (/Type /Page) - funktioniert bei unkomprimierten PDFs,
 *      wie sie Word, Scanner und die meisten Druckertreiber erzeugen.
 *   2. /Count aus dem Seitenbaum (/Type /Pages) - greift, wenn die Seiten-Objekte selbst
 *      nicht auffindbar sind. Bewusst NICHT irgendein /Count, da auch Lesezeichen
 *      (Outlines) ein /Count tragen und dessen Wert nichts mit Seiten zu tun hat.
 *
 * Liefern beide Wege ein Ergebnis, gewinnt der kleinere: inkrementell gespeicherte PDFs
 * (ausgefüllte Formulare, signierte Dokumente) enthalten die Seiten-Objekte älterer
 * Versionen weiterhin, Strategie 1 zählt sie mit und überschätzt dann deutlich.
 *
 * @returns {number|null} Seitenzahl oder null, wenn die Datei nicht lesbar war.
 */
export const readPdfPageCount = async (file) => {
  if (!file || file.size > MAX_PARSE_BYTES) return null;

  let raw;
  try {
    const buffer = await file.arrayBuffer();
    // latin1: 1 Byte = 1 Zeichen, damit die Byte-Offsets der Struktur erhalten bleiben.
    raw = new TextDecoder('latin1').decode(new Uint8Array(buffer));
  } catch (error) {
    console.warn('PDF konnte für die Token-Schätzung nicht gelesen werden:', error);
    return null;
  }

  // 1. Seiten-Objekte zählen. [^s] schließt /Pages (den Seitenbaum) aus.
  const pageObjects = (raw.match(/\/Type\s*\/Page[^s]/g) || []).length;

  // 2. /Count im Seitenbaum suchen - beide Schlüssel-Reihenfolgen, da die Reihenfolge
  //    im Dictionary nicht festgelegt ist. Der größte Wert ist die Wurzel des Baums,
  //    Zwischenknoten zählen nur ihren eigenen Teilbaum.
  let treeCount = 0;
  const patterns = [
    /\/Type\s*\/Pages\b[\s\S]{0,500}?\/Count\s+(\d+)/g,
    /\/Count\s+(\d+)[\s\S]{0,500}?\/Type\s*\/Pages\b/g,
  ];
  for (const pattern of patterns) {
    let match;
    while ((match = pattern.exec(raw)) !== null) {
      treeCount = Math.max(treeCount, parseInt(match[1], 10) || 0);
    }
  }

  if (pageObjects > 0 && treeCount > 0) return Math.min(pageObjects, treeCount);
  return pageObjects || treeCount || null;
};

/**
 * Schätzt die Tokens einer ausgewählten Datei.
 *
 * @param {File} file Die ausgewählte Datei
 * @param {{ visionMode?: boolean }} options visionMode = App verarbeitet PDFs als Bilder
 * @returns {Promise<{tokens: number, pages: number|null, basis: 'pages'|'size'}>}
 *          basis gibt an, wie sicher die Schätzung ist: 'pages' = Seitenzahl gelesen,
 *          'size' = nur über die Dateigröße geraten.
 */
export const estimateDocumentTokens = async (file, { visionMode = false } = {}) => {
  if (getFileExtension(file) !== '.pdf') {
    return { tokens: estimateBySize(file), pages: null, basis: 'size' };
  }

  const tokensPerPage = visionMode ? PDF_VISION_TOKENS_PER_PAGE : PDF_TEXT_TOKENS_PER_PAGE;
  const pageCount = await readPdfPageCount(file);

  if (pageCount) {
    return { tokens: pageCount * tokensPerPage, pages: pageCount, basis: 'pages' };
  }

  // Seitenzahl nicht lesbar -> über die Dateigröße auf Seiten schließen.
  const estimatedPages = Math.max(1, Math.round((file?.size || 0) / PDF_FALLBACK_BYTES_PER_PAGE));
  return { tokens: estimatedPages * tokensPerPage, pages: estimatedPages, basis: 'size' };
};
